import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { SIGNUPS_OPEN } from "../lib/flags";
import { refreshSignedIn, useHomePath } from "../lib/session";
import { ArrowIcon, CheckIcon, ClockIcon, Logo } from "../lib/ui";

interface OrgHit {
  id: string;
  name: string;
}

/** Sits on the always-dark brand pane, so these use fixed colours rather than
 *  the theme's tokens (whose "tint" colours are light in light mode). */
function BrandRow({ paid, title, sub, amount }: { paid: boolean; title: string; sub: string; amount: string }) {
  return (
    <div className="lr">
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: paid ? "#a7ba62" : "#2e2610",
          color: paid ? "#0e1007" : "#e5be66",
        }}
      >
        {paid ? <CheckIcon size={14} strokeWidth={2.6} /> : <ClockIcon size={14} />}
      </span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
        <div className="mono" style={{ fontSize: 12, color: "#8d8f83" }}>{sub}</div>
      </div>
      <div className="mono" style={{ fontSize: 15 }}>{amount}</div>
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const home = useHomePath();
  const [phase, setPhase] = useState<"email" | "code" | "pick">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [resent, setResent] = useState(false);
  const [orgs, setOrgs] = useState<OrgHit[]>([]);

  async function requestCode(opts: { resend: boolean }) {
    setBusy(true);
    setError("");
    setNote("");
    setResent(false);
    const res = await api.post<{ ok: boolean; devCode?: string }>(
      "/api/auth/request-otp",
      { email: email.trim() },
    );
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setPhase("code");
    setResent(opts.resend);
    setNote(
      res.data.devCode
        ? `Dev mode — your code is ${res.data.devCode}`
        : "If that email is connected to an organisation, a 6-digit code is on its way. It expires in 10 minutes.",
    );
    if (res.data.devCode) setCode(res.data.devCode);
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    // Signs the browser in server-side (HttpOnly cookie): no token reaches this code, only which
    // orgs can be picked next.
    const res = await api.post<{ multi: boolean; orgs: OrgHit[] }>(
      "/api/auth/verify-otp",
      { email: email.trim(), code: code.trim() },
    );
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.data.orgs.length === 0) {
      setError("No organisations or businesses are registered to that email.");
      return;
    }
    if (!res.data.multi) {
      refreshSignedIn();
      nav("/dashboard");
      return;
    }
    setOrgs(res.data.orgs);
    setPhase("pick");
  }

  async function pickOrg(orgId: string) {
    setBusy(true);
    setError("");
    const res = await api.post<{ ok: boolean }>("/api/auth/select-org", { orgId });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    refreshSignedIn();
    nav("/dashboard");
  }

  function useDifferentEmail() {
    setPhase("email");
    setCode("");
    setError("");
    setNote("");
    setResent(false);
  }

  return (
    <div className="split">
      {/* brand pane: always the inverted surface */}
      <section className="brandpane">
        <div style={{ alignSelf: "flex-start" }}>
          <Logo height={30} tone="dark" to={home} />
        </div>

        <div>
          <p className="display" style={{ fontSize: "min(56px, 11vw)", color: "var(--band-fg)", maxWidth: 520 }}>
            See who has paid, the moment they do.
          </p>
          <p className="lead" style={{ marginTop: 22, maxWidth: 460, fontSize: 18 }}>
            Your dashboard matches every PayNow payment to the request it paid.
          </p>
        </div>

        <div className="brandledger hide-sm" aria-hidden="true">
          <BrandRow paid title="Monthly donation" sub="FOOD-021 · Sofia N." amount="S$20.00" />
          <BrandRow paid title="Volunteer shirts" sub="FOOD-020 · Daniel T." amount="S$45.00" />
          <BrandRow paid={false} title="Pantry sponsorship" sub="FOOD-022 · Nurul H." amount="S$200.00" />
        </div>
      </section>

      {/* form pane */}
      <main className="formpane">
        <div className="formcol">
          {phase === "email" && (
            <>
              <h1 className="h1">Sign in</h1>
              <p className="body" style={{ margin: "14px 0 32px", fontSize: 17 }}>
                Enter the contact email you used when you connected your organisation. We will email you a 6-digit code. No password.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  requestCode({ resend: false });
                }}
              >
                <label className="lbl" htmlFor="si-email">Contact email</label>
                <input
                  id="si-email"
                  className="inp"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@organisation.org"
                />
                {error && <div className="error-box" role="alert">{error}</div>}
                <button className="btn btn--block" style={{ marginTop: 22, height: 52 }} disabled={busy || !email.trim()}>
                  {busy ? "Sending…" : "Send code"}
                </button>
              </form>
              {SIGNUPS_OPEN && (
                <p style={{ margin: "28px 0 0", fontSize: 15, color: "var(--ink2)" }}>
                  New here? <Link to="/onboarding" style={{ fontWeight: 600 }}>Connect your organisation</Link>
                </p>
              )}
            </>
          )}

          {phase === "code" && (
            <>
              <h1 className="h1">Check your email</h1>
              <p className="body" style={{ margin: "14px 0 28px", fontSize: 17 }}>{note}</p>
              {resent && (
                <div className="note note--ok" role="status" style={{ marginTop: 0, marginBottom: 20, fontSize: 14.5 }}>
                  <CheckIcon size={18} />
                  A new code is on its way.
                </div>
              )}
              <form onSubmit={verify}>
                <label className="lbl" htmlFor="si-code">6-digit code</label>
                <input
                  id="si-code"
                  className="inp codein"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                />
                {error && <div className="error-box" role="alert">{error}</div>}
                <button className="btn btn--block" style={{ marginTop: 22, height: 52 }} disabled={busy || code.length !== 6}>
                  {busy ? "Checking…" : "Verify and sign in"}
                </button>
              </form>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={useDifferentEmail}>
                  Use a different email
                </button>
                <button type="button" className="textbtn" style={{ fontSize: 15 }} disabled={busy} onClick={() => requestCode({ resend: true })}>
                  Resend code
                </button>
              </div>
            </>
          )}

          {phase === "pick" && (
            <>
              <h1 className="h1">Which organisation?</h1>
              <p className="body" style={{ margin: "14px 0 28px", fontSize: 17 }}>
                That email is connected to more than one. Pick the one to open.
              </p>
              {error && <div className="error-box" role="alert" style={{ marginTop: 0, marginBottom: 14 }}>{error}</div>}
              <div style={{ display: "grid", gap: 12 }}>
                {orgs.map((o) => (
                  <button key={o.id} type="button" className="orgrow" disabled={busy} onClick={() => pickOrg(o.id)}>
                    <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: "-0.01em" }}>{o.name}</span>
                    <ArrowIcon size={20} />
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 24 }}>
                <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={useDifferentEmail}>
                  Use a different email
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
